import * as React from 'react';
import {
  Autocomplete,
  Box,
  Collapse,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslate } from 'react-admin';
import { NumericTextField } from './NumericInputField';
import { TaskTemplateField, TaskTemplateRecord } from './types';

type ScheduleTaskMetaDataEditorProps = {
  taskTemplate: TaskTemplateRecord | null;
  value: Record<string, unknown> | null | undefined;
  onChange: (metaData: Record<string, unknown> | null) => void;
  disabled?: boolean;
};

const getMetaDataFields = (taskTemplate: TaskTemplateRecord | null): TaskTemplateField[] => (
  taskTemplate?.inputMeta?.metaData || []
);

const countFilledMetaDataFields = (
  fields: TaskTemplateField[],
  currentValue: Record<string, unknown>,
): number => fields.filter((field) => {

  const fieldValue = currentValue[field.name];
  return fieldValue !== undefined && fieldValue !== null && String(fieldValue) !== '';

}).length;

export const ScheduleTaskMetaDataEditor = ({
  taskTemplate,
  value,
  onChange,
  disabled,
}: ScheduleTaskMetaDataEditorProps) => {

  const translate = useTranslate();
  const fields = getMetaDataFields(taskTemplate);
  const currentValue = value || {};
  const filledCount = countFilledMetaDataFields(fields, currentValue);
  const [expanded, setExpanded] = React.useState(() => filledCount > 0);

  if (fields.length === 0) return null;

  const updateField = (fieldName: string, fieldValue: string | null) => {

    const next = { ...currentValue };

    if (fieldValue === null || fieldValue === '') {

      delete next[fieldName];

    } else {

      next[fieldName] = fieldValue;

    }

    onChange(Object.keys(next).length > 0 ? next : null);

  };

  return (
    <Box sx={{
      mt: 2, pl: 1, borderLeft: '3px solid', borderColor: 'divider',
    }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: expanded ? 1 : 0,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <IconButton
          size="small"
          aria-label={expanded
            ? translate('ra.action.close', { _: 'Collapse' })
            : translate('ra.action.expand', { _: 'Expand' })}
          onClick={(event) => {

            event.stopPropagation();
            setExpanded((prev) => !prev);

          }}
        >
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          {translate('resources.process-templates.text.meta_data', { _: 'Meta data' })}
          {filledCount > 0 ? ` (${filledCount})` : ''}
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pb: 1 }}>
          {fields.map((field) => {

            const fieldValue = currentValue[field.name];
            const stringValue = fieldValue === undefined || fieldValue === null ? '' : String(fieldValue);

            if (field.input.type === 'list') {

              const options = field.input.options || [];
              const selectedOption = options.find((option) => option.value === stringValue) || null;

              return (
                <Autocomplete
                  key={field.name}
                  options={options}
                  value={selectedOption}
                  disabled={disabled}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, val) => option.value === val.value}
                  onChange={(_, newValue) => updateField(field.name, newValue?.value ?? null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field.label}
                      placeholder={field.input.placeholder}
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              );

            }

            const isMultiline = field.input.type === 'textarea';
            const isNumeric = field.input.type === 'number';

            if (isNumeric) {

              return (
                <Box key={field.name} sx={{ mb: 2 }}>
                  <NumericTextField
                    fullWidth
                    label={field.label}
                    value={stringValue}
                    disabled={disabled}
                    placeholder={field.input.placeholder}
                    onChange={(nextValue) => updateField(field.name, nextValue || null)}
                  />
                </Box>
              );

            }

            return (
              <Box key={field.name} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  type={field.input.type === 'number' ? 'number' : 'text'}
                  label={field.label}
                  value={stringValue}
                  disabled={disabled}
                  multiline={isMultiline}
                  minRows={isMultiline ? 4 : undefined}
                  placeholder={field.input.placeholder}
                  onChange={(event) => updateField(field.name, event.target.value || null)}
                />
                {field.input.variables && field.input.variables.length > 0 && (
                  <FormHelperText>
                    {translate('resources.process-templates.text.meta_data_variables', {
                      _: 'Variables: %{variables}',
                      variables: field.input.variables.map((variable) => `%{${variable}}`).join(', '),
                    })}
                  </FormHelperText>
                )}
                {field.input.example && (
                  <FormHelperText sx={{ whiteSpace: 'pre-wrap' }}>
                    {`${translate('resources.process-templates.text.meta_data_example', { _: 'Example' })}: ${field.input.example}`}
                  </FormHelperText>
                )}
              </Box>
            );

          })}
        </Box>
      </Collapse>
    </Box>
  );

};
